import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/api';
import { 
  AlertTriangle, Database, RefreshCw, CheckCircle, 
  XCircle, Download, ArrowLeft, Shield, Scissors, Calendar
} from 'lucide-react';
import { Link } from 'react-router-dom';

const DataRecovery = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [manualBalance, setManualBalance] = useState('9074.60');
  const [recalculating, setRecalculating] = useState(false);
  const [recalculateResult, setRecalculateResult] = useState(null);
  const [settingCutoff, setSettingCutoff] = useState(false);
  const [cutoffResult, setCutoffResult] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  useEffect(() => {
    loadPreview();
  }, []);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/admin/recovery/taxisco/preview');
      setPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar datos de recuperación');
    } finally {
      setLoading(false);
    }
  };

  const executeRecovery = async () => {
    if (!window.confirm('¿Estás seguro de que deseas restaurar los datos? Esta acción insertará las transacciones recuperadas en la base de datos.')) {
      return;
    }

    try {
      setRecovering(true);
      setError(null);
      const response = await apiClient.post('/admin/recovery/taxisco/execute?confirm=true');
      setResult(response.data);
      // Recargar preview para ver el nuevo estado
      await loadPreview();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al ejecutar recuperación');
    } finally {
      setRecovering(false);
    }
  };

  const fixTimestamps = async () => {
    if (!window.confirm('¿Corregir los timestamps de los registros recuperados?')) {
      return;
    }

    try {
      setRecovering(true);
      setError(null);
      const response = await apiClient.post('/admin/recovery/taxisco/fix-timestamps');
      setResult(response.data);
      await loadPreview();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al corregir timestamps');
    } finally {
      setRecovering(false);
    }
  };

  const setBalance = async () => {
    const balance = parseFloat(manualBalance);
    if (isNaN(balance) || balance < 0) {
      setError('Por favor ingresa un balance válido');
      return;
    }
    
    if (!window.confirm(`¿Establecer el balance de Siempre Taxisco a Q${balance.toFixed(2)}?`)) {
      return;
    }

    try {
      setRecovering(true);
      setError(null);
      const response = await apiClient.post(`/admin/recovery/taxisco/set-balance?balance=${balance}`);
      setResult(response.data);
      await loadPreview();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al establecer balance');
    } finally {
      setRecovering(false);
    }
  };

  const recalculateAllBalances = async () => {
    if (!window.confirm(
      '¿Recalcular el balance de TODAS las tiendas?\n\n' +
      'Las tiendas con corte de balance (como Taxisco) calcularán desde su fecha de corte.\n\n' +
      'Esto sincronizará el balance de cada tienda con sus ventas y gastos reales.'
    )) {
      return;
    }

    try {
      setRecalculating(true);
      setError(null);
      setRecalculateResult(null);
      const response = await apiClient.post('/admin/recalculate-balances');
      setRecalculateResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al recalcular balances');
    } finally {
      setRecalculating(false);
    }
  };

  const setBalanceCutoff = async () => {
    const balance = parseFloat(manualBalance);
    if (isNaN(balance) || balance < 0) {
      setError('Por favor ingresa un balance válido');
      return;
    }

    if (!window.confirm(
      `¿Establecer CORTE DE BALANCE para Siempre Taxisco?\n\n` +
      `📅 Fecha de corte: 7 de Octubre 2026, 12:00 PM\n` +
      `💰 Balance de corte: Q${balance.toFixed(2)}\n\n` +
      `A partir de ahora, el sistema calculará:\n` +
      `Balance = Q${balance.toFixed(2)} + ventas_desde_corte - gastos_desde_corte\n\n` +
      `Los datos anteriores al corte NO afectarán el cálculo.`
    )) {
      return;
    }

    try {
      setSettingCutoff(true);
      setError(null);
      setCutoffResult(null);
      
      // Fecha: 7 Oct 2026, 12:00 PM Guatemala (18:00 UTC)
      const response = await apiClient.post(
        '/admin/store/5d7dbc8d-2bfc-4667-bdfe-ab9642f68319/set-balance-cutoff',
        {
          cutoff_date: '2026-10-07T18:00:00Z',
          cutoff_amount: balance,
          reason: 'Pérdida de datos históricos por reset accidental. Balance verificado antes del incidente.'
        }
      );
      setCutoffResult(response.data);
      await loadPreview();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al establecer corte de balance');
    } finally {
      setSettingCutoff(false);
    }
  };

  const runDiagnosis = async () => {
    try {
      setDiagnosing(true);
      setError(null);
      setDiagnosis(null);
      const response = await apiClient.get('/admin/recovery/taxisco/diagnose');
      setDiagnosis(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al ejecutar diagnóstico');
    } finally {
      setDiagnosing(false);
    }
  };

  const formatMoney = (amount) => {
    return `Q${(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <Shield className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Acceso Denegado</h1>
          <p className="text-gray-600">Solo administradores pueden acceder a esta página.</p>
          <Link to="/dashboard" className="mt-4 inline-block text-blue-600 hover:underline">
            Volver al Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/audit-transactions" className="text-blue-400 hover:text-blue-300 flex items-center mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Volver a Auditoría
          </Link>
          <h1 className="text-3xl font-bold text-white flex items-center">
            <Database className="mr-3 text-amber-400" size={32} />
            Recuperación de Datos
          </h1>
          <p className="text-slate-400 mt-2">
            Restaurar datos eliminados de Siempre Taxisco desde los registros de auditoría
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/50 border border-red-500 rounded-xl p-4 mb-6 flex items-start">
            <XCircle className="text-red-400 mr-3 flex-shrink-0" size={24} />
            <div>
              <p className="text-red-200 font-semibold">Error</p>
              <p className="text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Resultado de recuperación */}
        {result && (
          <div className="bg-emerald-900/50 border border-emerald-500 rounded-xl p-6 mb-6">
            <div className="flex items-start">
              <CheckCircle className="text-emerald-400 mr-3 flex-shrink-0" size={32} />
              <div>
                <h3 className="text-xl font-bold text-emerald-200 mb-2">
                  ✅ {result.sales_fixed !== undefined ? 'Corrección Completada' : 'Recuperación Completada'}
                </h3>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-emerald-800/50 rounded-lg p-3">
                    <p className="text-emerald-300 text-sm">
                      {result.sales_fixed !== undefined ? 'Ventas Corregidas' : 'Ventas Restauradas'}
                    </p>
                    <p className="text-2xl font-bold text-white">
                      {result.sales_fixed !== undefined ? result.sales_fixed : result.recovered?.sales || 0}
                    </p>
                  </div>
                  <div className="bg-emerald-800/50 rounded-lg p-3">
                    <p className="text-emerald-300 text-sm">
                      {result.sales_fixed !== undefined ? 'Gastos Corregidos' : 'Gastos Restaurados'}
                    </p>
                    <p className="text-2xl font-bold text-white">
                      {result.sales_fixed !== undefined ? result.expenses_fixed : result.recovered?.expenses || 0}
                    </p>
                  </div>
                </div>
                <p className="text-emerald-200 mt-4">
                  Nuevo balance de la tienda: <strong>{formatMoney(result.new_balance)}</strong>
                </p>
                {result.calculation && (
                  <div className="mt-4 bg-slate-800/50 rounded-lg p-3">
                    <p className="text-slate-300 text-sm font-semibold">Cálculo del balance:</p>
                    <p className="text-slate-400 text-xs">
                      Saldo inicial: {formatMoney(result.calculation.initial_balance)} + 
                      Ventas efectivo: {formatMoney(result.calculation.cash_sales)} - 
                      Gastos efectivo: {formatMoney(result.calculation.cash_expenses)}
                    </p>
                  </div>
                )}
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-4 bg-yellow-900/50 rounded-lg p-3">
                    <p className="text-yellow-300 text-sm font-semibold">Algunos errores ocurrieron:</p>
                    {result.errors.slice(0, 5).map((err, idx) => (
                      <p key={idx} className="text-yellow-200 text-xs">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="bg-slate-800 rounded-xl p-12 text-center">
            <RefreshCw className="animate-spin mx-auto text-blue-400 mb-4" size={48} />
            <p className="text-slate-300">Cargando datos de recuperación...</p>
          </div>
        )}

        {/* Preview */}
        {!loading && preview && (
          <div className="space-y-6">
            {/* Advertencia */}
            <div className="bg-amber-900/50 border border-amber-500 rounded-xl p-4 flex items-start">
              <AlertTriangle className="text-amber-400 mr-3 flex-shrink-0" size={24} />
              <div>
                <p className="text-amber-200 font-semibold">Información Importante</p>
                <p className="text-amber-300 text-sm mt-1">
                  Los datos fueron extraídos de los registros de auditoría (audit_logs). 
                  No todos los datos originales pudieron ser recuperados ya que los logs tienen un límite de retención.
                </p>
              </div>
            </div>

            {/* Estado actual */}
            <div className="bg-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                <Database className="mr-2 text-blue-400" size={20} />
                Estado Actual de {preview.store_name}
              </h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700 rounded-lg p-4 text-center">
                  <p className="text-slate-400 text-sm">Ventas Actuales</p>
                  <p className="text-2xl font-bold text-white">{preview.current_state.sales}</p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4 text-center">
                  <p className="text-slate-400 text-sm">Gastos Actuales</p>
                  <p className="text-2xl font-bold text-white">{preview.current_state.expenses}</p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4 text-center">
                  <p className="text-slate-400 text-sm">Balance Actual</p>
                  <p className="text-2xl font-bold text-emerald-400">{formatMoney(preview.current_state.balance)}</p>
                </div>
              </div>
            </div>

            {/* Datos recuperables */}
            <div className="bg-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center">
                <RefreshCw className="mr-2 text-amber-400" size={20} />
                Datos Recuperables
              </h2>
              
              <div className="grid grid-cols-2 gap-6">
                {/* Ventas */}
                <div className="bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 rounded-xl p-5 border border-emerald-600/30">
                  <h3 className="text-emerald-300 font-semibold mb-3">Ventas</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Originales perdidas:</span>
                      <span className="text-white font-bold">{preview.original_lost.sales.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Recuperables:</span>
                      <span className="text-emerald-400 font-bold">{preview.recoverable.sales.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Porcentaje:</span>
                      <span className="text-amber-400 font-bold">{preview.recovery_percentage.sales}%</span>
                    </div>
                    <hr className="border-slate-600 my-2" />
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monto total:</span>
                      <span className="text-emerald-300 font-bold">{formatMoney(preview.recoverable.sales_amount)}</span>
                    </div>
                  </div>
                </div>

                {/* Gastos */}
                <div className="bg-gradient-to-br from-red-900/50 to-red-800/30 rounded-xl p-5 border border-red-600/30">
                  <h3 className="text-red-300 font-semibold mb-3">Gastos</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Originales perdidos:</span>
                      <span className="text-white font-bold">{preview.original_lost.expenses.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Recuperables:</span>
                      <span className="text-red-400 font-bold">{preview.recoverable.expenses.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Porcentaje:</span>
                      <span className="text-amber-400 font-bold">{preview.recovery_percentage.expenses}%</span>
                    </div>
                    <hr className="border-slate-600 my-2" />
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monto total:</span>
                      <span className="text-red-300 font-bold">{formatMoney(preview.recoverable.expenses_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botón de recuperación */}
              {preview.current_state.sales === 0 && preview.current_state.expenses === 0 && (
                <div className="mt-6">
                  <button
                    onClick={executeRecovery}
                    disabled={recovering}
                    className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-3 transition-all ${
                      recovering 
                        ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-lg hover:shadow-amber-500/25'
                    }`}
                  >
                    {recovering ? (
                      <>
                        <RefreshCw className="animate-spin" size={24} />
                        <span>Recuperando datos...</span>
                      </>
                    ) : (
                      <>
                        <Download size={24} />
                        <span>🔄 EJECUTAR RECUPERACIÓN</span>
                      </>
                    )}
                  </button>
                  <p className="text-center text-slate-500 text-sm mt-2">
                    Esta acción restaurará {preview.recoverable.sales} ventas y {preview.recoverable.expenses} gastos
                  </p>
                </div>
              )}

              {/* Si ya hay datos */}
              {(preview.current_state.sales > 0 || preview.current_state.expenses > 0) && (
                <div className="mt-6 space-y-4">
                  <div className="bg-blue-900/50 border border-blue-500 rounded-xl p-4">
                    <p className="text-blue-200">
                      <strong>Estado:</strong> La tienda tiene {preview.current_state.sales} ventas y {preview.current_state.expenses} gastos registrados.
                    </p>
                  </div>
                  
                  {/* Botón para corregir timestamps y recalcular balance */}
                  <button
                    onClick={fixTimestamps}
                    disabled={recovering}
                    className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-3 transition-all ${
                      recovering 
                        ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 shadow-lg hover:shadow-blue-500/25'
                    }`}
                  >
                    {recovering ? (
                      <>
                        <RefreshCw className="animate-spin" size={24} />
                        <span>Corrigiendo...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw size={24} />
                        <span>🔧 CORREGIR TIMESTAMPS</span>
                      </>
                    )}
                  </button>
                  <p className="text-center text-slate-500 text-sm">
                    Esto corregirá los registros para que aparezcan en los reportes
                  </p>

                  {/* Campo para establecer balance manualmente */}
                  <div className="bg-amber-900/30 border border-amber-500 rounded-xl p-4 mt-6">
                    <h3 className="text-amber-200 font-semibold mb-3 flex items-center">
                      <Scissors className="mr-2" size={20} />
                      Establecer Corte de Balance
                    </h3>
                    <p className="text-amber-300/80 text-sm mb-4">
                      Establece un <strong>corte de balance</strong> para Taxisco. El sistema calculará el saldo 
                      solo desde la fecha del corte (7 Oct 2026, 12:00 PM), ignorando los datos históricos perdidos.
                    </p>
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="text-white font-semibold">Q</span>
                      <input
                        type="number"
                        step="0.01"
                        value={manualBalance}
                        onChange={(e) => setManualBalance(e.target.value)}
                        className="flex-1 px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white text-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="9074.60"
                      />
                    </div>
                    <button
                      onClick={setBalanceCutoff}
                      disabled={settingCutoff}
                      className={`w-full px-6 py-3 rounded-lg font-bold flex items-center justify-center space-x-2 transition-all ${
                        settingCutoff 
                          ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
                      }`}
                    >
                      {settingCutoff ? (
                        <>
                          <RefreshCw className="animate-spin" size={20} />
                          <span>Estableciendo corte...</span>
                        </>
                      ) : (
                        <>
                          <Scissors size={20} />
                          <span>ESTABLECER CORTE DE BALANCE</span>
                        </>
                      )}
                    </button>
                    
                    {cutoffResult && (
                      <div className="mt-3 bg-emerald-900/50 border border-emerald-500 rounded-lg p-3">
                        <p className="text-emerald-300 text-sm">
                          ✅ {cutoffResult.message}
                        </p>
                      </div>
                    )}
                    
                    <p className="text-slate-400 text-xs mt-3">
                      <Calendar size={14} className="inline mr-1" />
                      Fecha de corte: 7 de Octubre 2026, 12:00 PM (hora del incidente)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sincronizar balances de TODAS las tiendas - SIEMPRE VISIBLE */}
        <div className="bg-slate-800/50 border border-slate-600 rounded-xl p-6 mt-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center">
            <RefreshCw className="mr-3 text-blue-400" size={24} />
            Sincronizar Balances de Todas las Tiendas
          </h3>
          <p className="text-slate-300 text-sm mb-4">
            Recalcula el balance de todas las tiendas basándose en sus ventas y gastos reales.
            <br />
            <strong className="text-amber-300">⚠️ Siempre Taxisco será excluida</strong> porque su balance fue ajustado manualmente (Q9,074.60).
          </p>
          <button
            onClick={recalculateAllBalances}
            disabled={recalculating}
            className={`w-full px-6 py-4 rounded-lg font-bold flex items-center justify-center space-x-3 transition-all ${
              recalculating 
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700 shadow-lg'
            }`}
          >
            {recalculating ? (
              <>
                <RefreshCw className="animate-spin" size={24} />
                <span>Recalculando balances...</span>
              </>
            ) : (
              <>
                <RefreshCw size={24} />
                <span>🔄 RECALCULAR TODOS LOS BALANCES</span>
              </>
            )}
          </button>
          
          {recalculateResult && (
            <div className="mt-4 bg-emerald-900/30 border border-emerald-500 rounded-lg p-4">
              <p className="text-emerald-400 font-semibold mb-2">
                ✅ {recalculateResult.message}
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p className="text-slate-300">Tiendas procesadas:</p>
                <p className="text-white font-semibold">{recalculateResult.total_stores}</p>
                <p className="text-slate-300">Tiendas ajustadas:</p>
                <p className="text-white font-semibold">{recalculateResult.stores_adjusted}</p>
              </div>
              {recalculateResult.stores_excluded?.length > 0 && (
                <div className="mt-3 text-amber-300 text-sm">
                  <p className="font-semibold">Tiendas excluidas (balance manual):</p>
                  <ul className="list-disc list-inside">
                    {recalculateResult.stores_excluded.map((s, i) => (
                      <li key={i}>{s.name}: Q{s.balance?.toLocaleString()}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Diagnóstico de Ventas */}
        <div className="bg-slate-800/50 border border-slate-600 rounded-xl p-6 mt-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center">
            <AlertTriangle className="mr-3 text-yellow-400" size={24} />
            Diagnóstico de Ventas - Taxisco
          </h3>
          <p className="text-slate-300 text-sm mb-4">
            Si las ventas de Taxisco no aparecen en el filtro, usa este diagnóstico para identificar el problema.
          </p>
          <button
            onClick={runDiagnosis}
            disabled={diagnosing}
            className={`w-full px-6 py-3 rounded-lg font-bold flex items-center justify-center space-x-3 transition-all ${
              diagnosing 
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-yellow-600 to-amber-600 text-white hover:from-yellow-700 hover:to-amber-700'
            }`}
          >
            {diagnosing ? (
              <>
                <RefreshCw className="animate-spin" size={20} />
                <span>Analizando...</span>
              </>
            ) : (
              <>
                <AlertTriangle size={20} />
                <span>EJECUTAR DIAGNÓSTICO</span>
              </>
            )}
          </button>
          
          {diagnosis && (
            <div className="mt-4 bg-slate-900/50 rounded-lg p-4 space-y-4">
              <div className={`p-3 rounded-lg ${
                diagnosis.summary.sales_without_timestamp > 0 
                  ? 'bg-red-900/30 border border-red-500' 
                  : diagnosis.summary.sales_after_cutoff === 0 
                    ? 'bg-amber-900/30 border border-amber-500'
                    : 'bg-emerald-900/30 border border-emerald-500'
              }`}>
                <p className="font-semibold">{diagnosis.diagnostic_message}</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div className="bg-slate-800 rounded-lg p-3">
                  <p className="text-2xl font-bold text-white">{diagnosis.summary.total_sales}</p>
                  <p className="text-slate-400 text-xs">Total Ventas</p>
                </div>
                <div className="bg-emerald-900/30 rounded-lg p-3">
                  <p className="text-2xl font-bold text-emerald-400">{diagnosis.summary.sales_after_cutoff}</p>
                  <p className="text-slate-400 text-xs">Después del Corte</p>
                </div>
                <div className="bg-amber-900/30 rounded-lg p-3">
                  <p className="text-2xl font-bold text-amber-400">{diagnosis.summary.sales_before_cutoff}</p>
                  <p className="text-slate-400 text-xs">Antes del Corte</p>
                </div>
                <div className="bg-red-900/30 rounded-lg p-3">
                  <p className="text-2xl font-bold text-red-400">{diagnosis.summary.sales_without_timestamp}</p>
                  <p className="text-slate-400 text-xs">Sin Timestamp</p>
                </div>
              </div>
              
              {diagnosis.balance_cutoff.date && (
                <div className="text-sm text-slate-400">
                  <p>📅 Fecha de corte: <span className="text-white">{new Date(diagnosis.balance_cutoff.date).toLocaleString()}</span></p>
                  <p>💰 Balance de corte: <span className="text-white">Q{diagnosis.balance_cutoff.amount?.toLocaleString()}</span></p>
                </div>
              )}
              
              {/* Cajeros asignados a Taxisco */}
              {diagnosis.users_assigned_to_taxisco?.length > 0 && (
                <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-3">
                  <p className="text-blue-400 font-semibold mb-2">👥 Usuarios asignados a Taxisco:</p>
                  <div className="space-y-1 text-sm">
                    {diagnosis.users_assigned_to_taxisco.map((user, i) => (
                      <div key={i} className="flex justify-between text-slate-300">
                        <span>{user.name} ({user.username})</span>
                        <span className="text-slate-500">{user.role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {diagnosis.sales_after_cutoff?.length > 0 && (
                <div>
                  <p className="text-emerald-400 font-semibold mb-2">✅ Ventas Visibles (después del corte):</p>
                  <div className="max-h-40 overflow-y-auto bg-slate-800/50 rounded p-2 text-xs">
                    {diagnosis.sales_after_cutoff.map((sale, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-slate-700">
                        <span className="text-slate-300">{sale.description || sale.method}</span>
                        <span className="text-emerald-400">Q{sale.amount}</span>
                        <span className="text-slate-400">{sale.created_by}</span>
                        <span className="text-slate-500">{new Date(sale.timestamp).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {diagnosis.sales_without_timestamp?.length > 0 && (
                <div>
                  <p className="text-red-400 font-semibold mb-2">⚠️ Ventas SIN Timestamp (invisibles):</p>
                  <div className="max-h-40 overflow-y-auto bg-red-900/20 rounded p-2 text-xs">
                    {diagnosis.sales_without_timestamp.map((sale, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-red-900">
                        <span className="text-slate-300">{sale.description || sale.method}</span>
                        <span className="text-red-400">Q{sale.amount}</span>
                        <span className="text-slate-400">{sale.created_by}</span>
                        <span className="text-slate-500">Sin fecha</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-amber-300 text-xs mt-2">
                    💡 Usa el botón "Corregir Timestamps" más arriba para arreglar estas ventas.
                  </p>
                </div>
              )}
              
              {/* Ventas recientes de TODAS las tiendas */}
              {diagnosis.recent_sales_all_stores?.length > 0 && (
                <div className="border-t border-slate-700 pt-4">
                  <p className="text-cyan-400 font-semibold mb-2">🔍 Ventas recientes (todas las tiendas - últimos 7 días):</p>
                  <p className="text-slate-400 text-xs mb-2">Si ves ventas que deberían ser de Taxisco pero aparecen en otra tienda, el cajero puede estar mal asignado.</p>
                  <div className="max-h-48 overflow-y-auto bg-slate-800/50 rounded p-2 text-xs">
                    <div className="grid grid-cols-5 gap-2 font-semibold text-slate-400 mb-2 border-b border-slate-600 pb-1">
                      <span>Tienda</span>
                      <span>Monto</span>
                      <span>Método</span>
                      <span>Creado por</span>
                      <span>Fecha</span>
                    </div>
                    {diagnosis.recent_sales_all_stores.map((sale, i) => (
                      <div key={i} className={`grid grid-cols-5 gap-2 py-1 border-b border-slate-700/50 ${
                        sale.store_name?.includes('Taxisco') ? 'bg-emerald-900/20' : ''
                      }`}>
                        <span className="text-slate-300 truncate">{sale.store_name}</span>
                        <span className="text-cyan-400">Q{sale.amount}</span>
                        <span className="text-slate-400">{sale.method}</span>
                        <span className="text-slate-400">{sale.created_by}</span>
                        <span className="text-slate-500">{sale.timestamp ? new Date(sale.timestamp).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataRecovery;
